import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { backupsApi } from '@/api/backups';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import { idempotencyKey } from '@/utils/idempotency';
import type { BackupSettings, EnqueueResult, RestoreToHostRequest } from '@/types/api';

/** Every site's copies, newest first, a page at a time. */
export function useBackups() {
  return useInfiniteQuery({
    queryKey: queryKeys.backups,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => backupsApi.list(pageParam),
    getNextPageParam: (last) => last.nextCursor,
  });
}

/** Each server's share of the copies (ADR-0056), asked again every minute: copies come and rotate on their own. */
export function useBackupsOverview() {
  return useQuery({ queryKey: queryKeys.backupsOverview, queryFn: backupsApi.overview, refetchInterval: 60_000 });
}

/** Sites with their copies; the list stays on screen while a new filter is fetched. */
export function useBackupSites(filter: { serverId: string | null; q: string; problems: boolean }) {
  return useQuery({
    queryKey: queryKeys.backupSites(filter),
    queryFn: () => backupsApi.sites(filter),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useSaveBackupSettings(callbacks?: MutationCallbacks<BackupSettings, BackupSettings>) {
  return useApiMutation((settings: BackupSettings) => backupsApi.saveSettings(settings), () => [queryKeys.backups], callbacks);
}

export function useRestoreToHost(callbacks?: MutationCallbacks<EnqueueResult, { backupId: string; input: RestoreToHostRequest }>) {
  return useApiMutation(
    ({ backupId, input }: { backupId: string; input: RestoreToHostRequest }) => backupsApi.restoreToHost(backupId, input, idempotencyKey('site.host_restore')),
    () => [queryKeys.backups, queryKeys.jobs.all],
    callbacks,
  );
}

/** Where the browser downloads a part of a copy from, with the session it already has. */
export const backupDownloadUrl = backupsApi.downloadUrl;
